import ApiBase from './base/ApiBase';
import { buildSupportContextUrl } from '../help/catalog';

export type SupportRequestType = 'Issue' | 'FeatureRequest';
export type SupportRequestPriority = 'Low' | 'Normal' | 'High' | 'Urgent';
export type SupportRequestStatus =
    | 'Submitted'
    | 'WaitingForClient'
    | 'Triaged'
    | 'Planned'
    | 'InProgress'
    | 'Resolved'
    | 'NotPlanned'
    | 'Duplicate'
    | 'Closed';

export interface SupportRequestActivity {
    Id: string;
    ActivityType: string;
    Message?: string;
    FromStatus?: SupportRequestStatus;
    ToStatus?: SupportRequestStatus;
    IsInternal: boolean;
    ActorName: string;
    CreatedDate: string;
}

export interface SupportRequestSummary {
    Id: string;
    Number: number;
    OrganizationId: string;
    OrganizationName: string;
    ReporterName: string;
    Type: SupportRequestType;
    Title: string;
    Description: string;
    Priority: SupportRequestPriority;
    Status: SupportRequestStatus;
    AssignedToSystemUserId?: string;
    AssignedToName?: string;
    CreatedDate: string;
    LastUpdateDate: string;
    ClosedDate?: string;
    RowVersion: string;
    Activity?: SupportRequestActivity[];
}

export interface CreateSupportRequest {
    Type: SupportRequestType;
    Title: string;
    Description: string;
    Priority: SupportRequestPriority;
    Impact?: 'Minor' | 'Degraded' | 'Blocked';
    BusinessImpact?: string;
    ReproductionSteps?: string;
    ExpectedBehavior?: string;
    ActualBehavior?: string;
    /** Stable, non-sensitive product-area identifier. Raw browser URLs are never sent. */
    RouteId?: string;
}

export interface AdminDashboardSummary {
    TotalOrganizations: number;
    ActiveSubscriptions: number;
    ActiveTeammates: number;
    ActiveUsers: number;
    OpenRequests: number;
    UrgentRequests: number;
    OpenIssues: number;
    OpenFeatureRequests: number;
    EstimatedMonthlyRevenueCents: number;
    EstimatedAnnualRevenueCents: number;
    Currency: string;
}

export interface AzureCostDailyPoint {
    Date: string;
    Cost: number;
}

export interface AzureCostBreakdown {
    Name: string;
    Cost: number;
    Percent: number;
}

export interface AzureSubscriptionCostBreakdown extends AzureCostBreakdown {
    SubscriptionId: string;
}

export interface AzureResourceCostBreakdown {
    SubscriptionId: string;
    SubscriptionName: string;
    ResourceId: string;
    Name: string;
    ResourceType: string;
    ResourceGroup: string;
    ServiceName: string;
    Category: string;
    Cost: number;
    Percent: number;
}

export interface AzureCostDashboard {
    Period: string;
    PeriodLabel: string;
    PeriodStart: string;
    PeriodEnd: string;
    DataThroughDate: string;
    Currency: string;
    ActualCost: number;
    PreviousPeriodCost: number;
    PreviousPeriodLabel: string;
    ChangeAmount: number;
    ChangePercent?: number;
    AverageDailyCost: number;
    ProjectedMonthEndCost: number;
    ProjectionSource: 'AzureForecast' | 'RunRate' | 'Actual';
    RetrievedAt: string;
    IsStale: boolean;
    IsEstimate: boolean;
    AvailablePeriods: string[];
    DailyCosts: AzureCostDailyPoint[];
    CategoryCosts: AzureCostBreakdown[];
    ServiceCosts: AzureCostBreakdown[];
    SubscriptionCosts: AzureSubscriptionCostBreakdown[];
    ResourceCosts: AzureResourceCostBreakdown[];
}

export interface AdminOrganizationSummary {
    Id: string;
    Name: string;
    IsSubscriptionActive: boolean;
    SubscriptionStatus: string;
    SubscriptionNotRequired: boolean;
    ActiveTeammateCount: number;
    InactiveTeammateCount: number;
    BilledTeammateCount: number;
    UserCount: number;
    OpenRequestCount: number;
    OpenIssueCount: number;
    OpenFeatureRequestCount: number;
    EstimatedMonthlyRevenueCents: number;
    LastActivityDate: string;
}

export interface SupportAssignee {
    Id: string;
    Name: string;
    EmailAddress: string;
}

export interface AdminRequestFilters {
    Query?: string;
    Status?: SupportRequestStatus | 'All';
    Type?: SupportRequestType | 'All';
    Priority?: SupportRequestPriority | 'All';
    OrganizationId?: string;
}

interface PagedResponse<T> {
    Items: T[];
    Page: number;
    TotalPages: number;
}

interface ApiSupportSummary {
    Id: string;
    RequestNumber: number;
    OrganizationId: string;
    OrganizationName?: string;
    ReporterName: string;
    RequestType: SupportRequestType;
    Title: string;
    Description: string;
    Priority: SupportRequestPriority;
    Status: SupportRequestStatus;
    AssignedToSystemUserId?: string;
    AssigneeName?: string;
    CreatedDate: string;
    LastUpdateDate: string;
    ClosedDate?: string;
    RowVersion: string;
}

interface ApiSupportComment {
    Id: string;
    AuthorName: string;
    Body: string;
    IsInternal: boolean;
    CreatedDate: string;
}

interface ApiSupportActivity {
    Id: string;
    ActorName: string;
    ActivityType: string;
    PreviousValue?: string;
    NewValue?: string;
    Details?: string;
    CreatedDate: string;
}

interface ApiSupportDetail {
    Summary: ApiSupportSummary;
    Description: string;
    Comments: ApiSupportComment[];
    Activities: ApiSupportActivity[];
}

interface ApiDashboardSummary {
    TotalOrganizations: number;
    ActiveSubscriptions: number;
    TotalUsers: number;
    ActiveTeammates: number;
    OpenRequests: number;
    OpenIssues: number;
    OpenFeatureRequests: number;
    UrgentRequests: number;
    MonthlyRecurringRevenueCents: number;
    AnnualRecurringRevenueCents: number;
    Currency: string;
}

interface ApiOrganizationSummary {
    OrganizationId: string;
    OrganizationName: string;
    UserCount: number;
    ActiveTeammateCount: number;
    InactiveTeammateCount: number;
    SubscriptionStatus: string;
    SubscriptionNotRequired: boolean;
    BilledQuantity: number;
    MonthlyRecurringRevenueCents: number;
    OpenIssueCount: number;
    OpenFeatureRequestCount: number;
    LastUpdateDate: string;
}

const queryString = (values: Record<string, string | undefined>) => {
    const query = new URLSearchParams();
    Object.entries(values).forEach(([key, value]) => value && value !== 'All' && query.append(key, value));
    const encoded = query.toString();
    return encoded ? `?${encoded}` : '';
};

const mapSummary = (summary: ApiSupportSummary, description = summary.Description || '', activity: SupportRequestActivity[] = []): SupportRequestSummary => ({
    Id: summary.Id,
    Number: summary.RequestNumber,
    OrganizationId: summary.OrganizationId,
    OrganizationName: summary.OrganizationName || '',
    ReporterName: summary.ReporterName,
    Type: summary.RequestType,
    Title: summary.Title,
    Description: description,
    Priority: summary.Priority,
    Status: summary.Status,
    AssignedToSystemUserId: summary.AssignedToSystemUserId,
    AssignedToName: summary.AssigneeName,
    CreatedDate: summary.CreatedDate,
    LastUpdateDate: summary.LastUpdateDate,
    ClosedDate: summary.ClosedDate,
    RowVersion: summary.RowVersion,
    Activity: activity,
});

const mapDetail = (detail: ApiSupportDetail): SupportRequestSummary => {
    const comments: SupportRequestActivity[] = (detail.Comments || []).map(comment => ({
        Id: comment.Id,
        ActivityType: 'CommentAdded',
        Message: comment.Body,
        IsInternal: comment.IsInternal,
        ActorName: comment.AuthorName,
        CreatedDate: comment.CreatedDate,
    }));
    const activities: SupportRequestActivity[] = (detail.Activities || []).map(activity => ({
        Id: activity.Id,
        ActivityType: activity.ActivityType,
        Message: activity.Details,
        FromStatus: activity.PreviousValue as SupportRequestStatus,
        ToStatus: activity.NewValue as SupportRequestStatus,
        IsInternal: true,
        ActorName: activity.ActorName,
        CreatedDate: activity.CreatedDate,
    }));
    return mapSummary(detail.Summary, detail.Description, [...comments, ...activities]);
};

class SupportClient extends ApiBase {
    private async GetAllPages<T>(pathForPage: (page: number) => string, signal?: AbortSignal): Promise<T[]> {
        const items: T[] = [];
        let page = 1;
        let totalPages = 1;
        do {
            const response = await this.GetAsync(pathForPage(page), signal) as PagedResponse<T>;
            items.push(...response.Items);
            totalPages = response.TotalPages;
            page += 1;
        } while (page <= totalPages);
        return items;
    }

    async GetRequests(signal?: AbortSignal): Promise<SupportRequestSummary[]> {
        const items = await this.GetAllPages<ApiSupportSummary>(page => `Support/Requests?Page=${page}&PageSize=100`, signal);
        return items.map(item => mapSummary(item));
    }

    async GetRequest(id: string, signal?: AbortSignal): Promise<SupportRequestSummary> {
        return mapDetail(await this.GetAsync(`Support/Requests/${id}`, signal) as ApiSupportDetail);
    }

    async CreateRequest(request: CreateSupportRequest, signal?: AbortSignal): Promise<SupportRequestSummary> {
        const response = await this.PostAsync('Support/Requests', {
            RequestType: request.Type,
            Title: request.Title,
            Description: request.Description,
            Priority: request.Priority,
            Impact: request.Impact || 'Minor',
            BusinessImpact: request.BusinessImpact,
            ReproductionSteps: request.ReproductionSteps,
            ExpectedBehavior: request.ExpectedBehavior,
            ActualBehavior: request.ActualBehavior,
            // The current backend stores context in PageUrl. Until its first-class
            // RouteId field is available, send a synthetic, query-free URL whose
            // only variable is a validated Help route identifier.
            PageUrl: request.RouteId ? buildSupportContextUrl(request.RouteId) : undefined,
            UserAgent: undefined,
        } as any, signal) as ApiSupportDetail;
        return mapDetail(response);
    }

    async AddComment(id: string, message: string, rowVersion: string, signal?: AbortSignal): Promise<SupportRequestSummary> {
        return mapDetail(await this.PostAsync(`Support/Requests/${id}/Comments`, {
            Body: message,
            IsInternal: false,
            RowVersion: rowVersion,
        } as any, signal) as ApiSupportDetail);
    }

    async CloseRequest(id: string, rowVersion: string, signal?: AbortSignal): Promise<SupportRequestSummary> {
        return mapDetail(await this.PostAsync(`Support/Requests/${id}/Close`, { RowVersion: rowVersion } as any, signal) as ApiSupportDetail);
    }

    async ReopenRequest(id: string, rowVersion: string, signal?: AbortSignal): Promise<SupportRequestSummary> {
        return mapDetail(await this.PostAsync(`Support/Requests/${id}/Reopen`, { RowVersion: rowVersion } as any, signal) as ApiSupportDetail);
    }

    async GetAdminDashboard(signal?: AbortSignal): Promise<AdminDashboardSummary> {
        const response = await this.GetAsync('Admin/Dashboard/Summary', signal) as ApiDashboardSummary;
        return {
            TotalOrganizations: response.TotalOrganizations,
            ActiveSubscriptions: response.ActiveSubscriptions,
            ActiveTeammates: response.ActiveTeammates,
            ActiveUsers: response.TotalUsers,
            OpenRequests: response.OpenRequests,
            UrgentRequests: response.UrgentRequests,
            OpenIssues: response.OpenIssues,
            OpenFeatureRequests: response.OpenFeatureRequests,
            EstimatedMonthlyRevenueCents: response.MonthlyRecurringRevenueCents,
            EstimatedAnnualRevenueCents: response.AnnualRecurringRevenueCents,
            Currency: response.Currency,
        };
    }

    GetAdminAzureCosts(period?: string, forceRefresh = false, signal?: AbortSignal): Promise<AzureCostDashboard> {
        const query = new URLSearchParams();
        if (period)
            query.set('period', period);
        if (forceRefresh)
            query.set('forceRefresh', 'true');
        const suffix = query.toString() ? `?${query.toString()}` : '';
        return this.GetAsync(`Admin/Dashboard/AzureCosts${suffix}`, signal);
    }

    async GetAdminOrganizations(signal?: AbortSignal): Promise<AdminOrganizationSummary[]> {
        const items = await this.GetAllPages<ApiOrganizationSummary>(page => `Admin/Organizations?Page=${page}&PageSize=100`, signal);
        return items.map(organization => ({
            Id: organization.OrganizationId,
            Name: organization.OrganizationName,
            IsSubscriptionActive: ['active', 'trialing'].includes(organization.SubscriptionStatus.toLowerCase()),
            SubscriptionStatus: organization.SubscriptionStatus,
            SubscriptionNotRequired: organization.SubscriptionNotRequired,
            ActiveTeammateCount: organization.ActiveTeammateCount,
            InactiveTeammateCount: organization.InactiveTeammateCount,
            BilledTeammateCount: organization.BilledQuantity,
            UserCount: organization.UserCount,
            OpenRequestCount: organization.OpenIssueCount + organization.OpenFeatureRequestCount,
            OpenIssueCount: organization.OpenIssueCount,
            OpenFeatureRequestCount: organization.OpenFeatureRequestCount,
            EstimatedMonthlyRevenueCents: organization.MonthlyRecurringRevenueCents,
            LastActivityDate: organization.LastUpdateDate,
        }));
    }

    async SetSubscriptionNotRequired(
        organizationId: string,
        subscriptionNotRequired: boolean,
        signal?: AbortSignal): Promise<void> {
        await this.PutAsync(
            `Admin/Organizations/${organizationId}/SubscriptionAccess`,
            { SubscriptionNotRequired: subscriptionNotRequired } as any,
            signal);
    }

    async GetAdminRequests(filters: AdminRequestFilters = {}, signal?: AbortSignal): Promise<SupportRequestSummary[]> {
        const items = await this.GetAllPages<ApiSupportSummary>(page => `Admin/Support/Requests${queryString({
            Search: filters.Query,
            Status: filters.Status,
            RequestType: filters.Type,
            Priority: filters.Priority,
            OrganizationId: filters.OrganizationId,
            Page: page.toString(),
            PageSize: '100',
        })}`, signal);
        return items.map(item => mapSummary(item));
    }

    async GetAdminRequest(id: string, signal?: AbortSignal): Promise<SupportRequestSummary> {
        return mapDetail(await this.GetAsync(`Admin/Support/Requests/${id}`, signal) as ApiSupportDetail);
    }

    GetAssignees(signal?: AbortSignal): Promise<SupportAssignee[]> {
        return this.GetAsync('Admin/Support/Assignees', signal);
    }

    async TransitionRequest(
        id: string,
        status: SupportRequestStatus,
        priority: SupportRequestPriority,
        rowVersion: string,
        reason?: string,
        duplicateOfSupportRequestId?: string): Promise<SupportRequestSummary> {
        return mapDetail(await this.PostAsync(`Admin/Support/Requests/${id}/Transitions`, {
            Status: status,
            Priority: priority,
            DuplicateOfSupportRequestId: duplicateOfSupportRequestId || null,
            Reason: reason || null,
            RowVersion: rowVersion,
        } as any) as ApiSupportDetail);
    }

    async AssignRequest(id: string, assignedToSystemUserId: string | undefined, rowVersion: string): Promise<SupportRequestSummary> {
        return mapDetail(await this.PostAsync(`Admin/Support/Requests/${id}/Assignments`, {
            AssignedToSystemUserId: assignedToSystemUserId || null,
            RowVersion: rowVersion,
        } as any) as ApiSupportDetail);
    }

    async AddAdminComment(id: string, message: string, isInternal: boolean, rowVersion: string): Promise<SupportRequestSummary> {
        return mapDetail(await this.PostAsync(`Admin/Support/Requests/${id}/Comments`, {
            Body: message,
            IsInternal: isInternal,
            RowVersion: rowVersion,
        } as any) as ApiSupportDetail);
    }
}

export default SupportClient;
